import React, { useState } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiPost } from '../../api/apiClient';
import { toastError } from '../common/toast';

const directions = [
  'North', 'South', 'East', 'West',
  'North-East', 'North-West', 'South-East', 'South-West'
];
const floors = [
  { value: 0, label: 'Ground Floor' },
  { value: 1, label: 'First Floor' },
  { value: 2, label: 'Second Floor' },
  { value: 3, label: 'Third Floor' },
  { value: 4, label: 'Fourth Floor' },
  { value: 5, label: 'Fifth Floor' }
];

const AddRoom = ({ buildingId, room, onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    beds: room?.beds || '',
    room_type: room?.room_type || '',
    floor: room?.floor >= 0 ? room.floor : '',
    direction: room?.direction || '',
    bed_rent: room?.bed_rent || '', // New field for Bed Rent
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const errs = {};
    // Only validate beds when creating a new room (not editing)
    if (!room && (!form.beds || Number(form.beds) <= 0)) errs.beds = 'Number of beds is required';
    if (!form.room_type) errs.room_type = 'Room type is required';
    if (!form.bed_rent || Number(form.bed_rent) <= 0) errs.bed_rent = 'Bed Rent is required'; // Validate Bed Rent
    return errs;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    try {
      if (form.floor === '') {
        form.floor = null; // Set floor to null if not selected
      }
      if (room !== null) {
        form.beds = room.beds.length; // Use existing room's beds
      }
      const payload = {
        ...form,
        beds: Number(form.beds),
        bed_rent: parseFloat(form.bed_rent), // Ensure Bed Rent is a decimal value
        building_id: buildingId,
        room_id: room?.room_id,
      };
      const res = await apiPost('/room', payload);
      if (res && res.data && res.data.room_id) {
        onSuccess && onSuccess();
      }
    } catch (e) {
      toastError('Failed to save room details ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rt-details-col" style={{ maxWidth: 500 }}>
      <h4>{room ? 'Edit Room' : 'Add Room'}</h4>
      <div className="rt-details-form-table">
        {!room && (
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">
              Number of Beds <span style={{ color: '#d32f2f' }}>*</span>
            </div>
            <div className="rt-details-form-value">
              <select
                value={form.beds}
                onChange={(e) => handleChange('beds', e.target.value)}
                className={`rt-input rt-dropdown${errors.beds ? ' rt-input-error' : ''}`} // Add rt-dropdown class
              >
                <option value="">Select</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              {errors.beds && <div className="rt-field-error">{errors.beds}</div>}
            </div>
          </div>
        )}
        <div className="rt-details-form-row">
          <div className="rt-details-form-label">
            Room Type <span style={{ color: '#d32f2f' }}>*</span>
          </div>
          <div className="rt-details-form-value">
            <select
              value={form.room_type}
              onChange={(e) => handleChange('room_type', e.target.value)}
              className={`rt-input rt-dropdown${errors.room_type ? ' rt-input-error' : ''}`} // Add rt-dropdown class
            >
              <option value="">Select</option>
              <option value="AC">AC</option>
              <option value="Non AC">Non AC</option>
            </select>
            {errors.room_type && <div className="rt-field-error">{errors.room_type}</div>}
          </div>
        </div>
        <div className="rt-details-form-row">
          <div className="rt-details-form-label">Floor</div>
          <div className="rt-details-form-value">
            <select
              value={form.floor}
              onChange={(e) => handleChange('floor', e.target.value)}
              className="rt-input rt-dropdown" // Add rt-dropdown class
            >
              <option value="">Select</option>
              {floors.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="rt-details-form-row">
          <div className="rt-details-form-label">Direction</div>
          <div className="rt-details-form-value">
            <select
              value={form.direction}
              onChange={(e) => handleChange('direction', e.target.value)}
              className="rt-input rt-dropdown" // Add rt-dropdown class
            >
              <option value="">Select</option>
              {directions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="rt-details-form-row">
          <div className="rt-details-form-label">
            Bed Rent <span style={{ color: '#d32f2f' }}>*</span>
          </div>
          <div className="rt-details-form-value">
            <input
              type="number"
              step="0.01" // Allow decimal values
              value={form.bed_rent}
              onChange={(e) => handleChange('bed_rent', e.target.value)}
              className={`rt-input${errors.bed_rent ? ' rt-input-error' : ''}`}
              placeholder="Enter Bed Rent"
            />
            {errors.bed_rent && <div className="rt-field-error">{errors.bed_rent}</div>}
          </div>
        </div>
        {errors.form && <div className="rt-field-error">{errors.form}</div>}
        <div style={{ textAlign: 'right', marginTop: 16, display: 'flex', gap: 10, justifyContent: 'center' }}>
          <RTButton variant="secondary" onClick={onCancel} disabled={saving}>Cancel</RTButton>
          <RTButton 
            variant="primary" 
            onClick={handleSubmit} 
            disabled={saving}
            style={{ 
              minHeight: '36px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {saving ? (
              <RTSpinner size="small" showMessage={false} />
            ) : (
              room ? 'Update' : 'Save'
            )}
          </RTButton>
        </div>
      </div>
    </div>
  );
};

export default AddRoom;